import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  memo,
} from "react";

import {
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
  CircularProgress,
  Chip,
  Divider,
  Fade,
} from "@mui/material";

import {
  Add,
  Search,
  Edit,
  Delete,
  Clear,
  ArrowBack,
  Store,
  PersonOutline,
  LockOutlined,
  PhoneOutlined,
  MailOutline,
} from "@mui/icons-material";

import { useNavigate } from "react-router-dom";

import clientsService from "../../api/clientsService";
import branchesService from "../../api/branchesService";

import { CLIENT_FIELDS } from "./constants";
import { cleanFormData, filterFields } from "./utils";

import {
  getBranchFormFields,
  getBranchEditFields,
  MESSAGES,
} from "../../utils/branchesConstants";

import { useSearchDebounce } from "../../hooks/useSearch";

import ModalFormUniversal from "../../components/ModalFormUniversal";
import ModalConfirmUniversal from "../../components/ModalConfirmUniversal";

// ─────────────────────────────────────────────────────────────
// COLORS
// ─────────────────────────────────────────────────────────────
const C = {
  primary: "#1975d1",
  primaryDark: "#125aa3",
  primarySoft: "#eef5fd",
  border: "#e5e7eb",
  bg: "#ffffff",
  bgSoft: "#f8fafc",
  text: "#111827",
  textSoft: "#6b7280",
  danger: "#dc2626",
  branch: "#0f9d6b",
  branchSoft: "#e8f7f0",
};

// ─────────────────────────────────────────────────────────────
// CUSTOM SCROLLBAR
// ─────────────────────────────────────────────────────────────
const SCROLL = {
  "&::-webkit-scrollbar": { width: 10, height: 10 },
  "&::-webkit-scrollbar-track": { background: "#f3f4f6", borderRadius: 999 },
  "&::-webkit-scrollbar-thumb": {
    background: "#c7d7ea",
    borderRadius: 999,
    border: "2px solid #f3f4f6",
  },
  "&::-webkit-scrollbar-thumb:hover": { background: C.primary },
  scrollbarWidth: "thin",
  scrollbarColor: "#c7d7ea #f3f4f6",
};

// ─────────────────────────────────────────────────────────────
// KPI
// ─────────────────────────────────────────────────────────────
const KPI = memo(function KPI({ label, value }) {
  return (
    <Box
      sx={{
        px: 2,
        py: 1.25,
        border: `1px solid ${C.border}`,
        borderRadius: 2,
        bgcolor: "#fff",
        minWidth: 110,
      }}
    >
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 700,
          color: C.textSoft,
          textTransform: "uppercase",
          letterSpacing: 0.4,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{ mt: 0.5, fontSize: 24, fontWeight: 700, color: C.text, lineHeight: 1 }}
      >
        {value}
      </Typography>
    </Box>
  );
});

// ─────────────────────────────────────────────────────────────
// RESULT ROW (unified: client or branch)
// ─────────────────────────────────────────────────────────────
const ResultRow = memo(function ResultRow({ type, title, subtitle, meta, onClick }) {
  const isBranch = type === "branch";
  const accent = isBranch ? C.branch : C.primary;
  const soft = isBranch ? C.branchSoft : C.primarySoft;

  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 2,
        py: 1.5,
        borderRadius: 2,
        border: `1px solid ${C.border}`,
        bgcolor: "#fff",
        cursor: "pointer",
        transition: "all .15s",
        "&:hover": {
          borderColor: accent,
          bgcolor: soft,
          transform: "translateY(-1px)",
          boxShadow: "0 4px 14px rgba(17,24,39,.06)",
        },
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: "50%",
          bgcolor: soft,
          color: accent,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {isBranch ? <Store fontSize="small" /> : <PersonOutline fontSize="small" />}
      </Box>

      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography noWrap sx={{ fontWeight: 700, fontSize: 14, color: C.text }}>
          {title}
        </Typography>
        <Typography noWrap sx={{ fontSize: 12.5, color: C.textSoft }}>
          {subtitle || "—"}
        </Typography>
      </Box>

      {meta && (
        <Typography
          noWrap
          sx={{ fontSize: 12, color: C.textSoft, maxWidth: 160, textAlign: "right" }}
        >
          {meta}
        </Typography>
      )}

      <Chip
        size="small"
        label={isBranch ? "Sucursal" : "Cliente"}
        sx={{
          height: 22,
          fontSize: 11,
          fontWeight: 700,
          bgcolor: soft,
          color: accent,
          border: `1px solid ${accent}33`,
        }}
      />
    </Box>
  );
});

// ─────────────────────────────────────────────────────────────
// BRANCH ROW (inside client detail)
// ─────────────────────────────────────────────────────────────
const BranchRow = memo(function BranchRow({ branch, highlighted, onOpen, onEdit, onDelete }) {
  return (
    <Box
      onClick={() => onOpen(branch)}
      sx={{
        display: "grid",
        gridTemplateColumns: "minmax(0,1.5fr) minmax(100px,.7fr) minmax(100px,.7fr) 90px",
        gap: 2,
        alignItems: "center",
        px: 2,
        py: 1.5,
        borderBottom: `1px solid ${C.border}`,
        width: "100%",
        boxSizing: "border-box",
        cursor: "pointer",
        transition: "background-color .15s",
        bgcolor: highlighted ? C.branchSoft : "transparent",
        "&:hover": { bgcolor: highlighted ? C.branchSoft : "#eef5fd" },
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography noWrap sx={{ fontWeight: 600, fontSize: 14, color: C.text }}>
          {branch.name}
        </Typography>
        <Typography noWrap sx={{ mt: 0.3, fontSize: 12, color: C.textSoft }}>
          {branch.address || "Sin dirección"}
        </Typography>
      </Box>

      <Typography noWrap sx={{ fontSize: 13, color: C.textSoft }}>
        {branch.city || "-"}
      </Typography>

      <Typography noWrap sx={{ fontSize: 13, color: C.textSoft }}>
        {branch.contactPhone || "-"}
      </Typography>

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.5 }}>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onEdit(branch);
          }}
        >
          <Edit sx={{ color: C.primary, fontSize: 18 }} />
        </IconButton>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(branch);
          }}
        >
          <Delete sx={{ color: C.danger, fontSize: 18 }} />
        </IconButton>
      </Box>
    </Box>
  );
});

// ─────────────────────────────────────────────────────────────
// MAIN
// ─────────────────────────────────────────────────────────────
export default function Clientes() {
  const navigate = useNavigate();

  // SEARCH
  const [query, setQuery] = useState("");
  const debounced = useSearchDebounce(query, 300);
  const [results, setResults] = useState({ clients: [], branches: [] });
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const reqId = useRef(0);

  // DETAIL
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientBranches, setClientBranches] = useState([]);
  const [branchesLoading, setBranchesLoading] = useState(false);
  const [highlightBranch, setHighlightBranch] = useState(null);
  const [branchQuery, setBranchQuery] = useState("");

  // CLIENT MODALS
  const [selClient, setSelClient] = useState(null);
  const [modalCliCreate, setModalCliCreate] = useState(false);
  const [modalCliEdit, setModalCliEdit] = useState(false);
  const [modalCliDelete, setModalCliDelete] = useState(false);

  // BRANCH MODALS
  const [selBranch, setSelBranch] = useState(null);
  const [modalBrCreate, setModalBrCreate] = useState(false);
  const [modalBrEdit, setModalBrEdit] = useState(false);
  const [modalBrDelete, setModalBrDelete] = useState(false);

  // ───────────────────────────────────────────────────────────
  // SERVER SEARCH (race-safe)
  // ───────────────────────────────────────────────────────────
  useEffect(() => {
    const term = debounced.trim();

    if (term.length < 2) {
      setResults({ clients: [], branches: [] });
      setSearching(false);
      setSearchError(null);
      return;
    }

    const id = ++reqId.current;
    setSearching(true);
    setSearchError(null);

    clientsService
      .search(term)
      .then((res) => {
        if (id !== reqId.current) return;
        const d = res.data?.data || {};
        setResults({
          clients: d.clients || [],
          branches: d.branches || [],
        });
      })
      .catch(() => {
        if (id !== reqId.current) return;
        setSearchError("No se pudo completar la búsqueda");
      })
      .finally(() => {
        if (id === reqId.current) setSearching(false);
      });
  }, [debounced]);

  const totalResults = results.clients.length + results.branches.length;

  // ───────────────────────────────────────────────────────────
  // LOAD BRANCHES OF SELECTED CLIENT
  // ───────────────────────────────────────────────────────────
  const loadBranches = useCallback(async (clientId) => {
    setBranchesLoading(true);
    try {
      const res = await branchesService.getByClient(clientId);
      setClientBranches(res.data?.data || []);
    } catch {
      setClientBranches([]);
    } finally {
      setBranchesLoading(false);
    }
  }, []);

  const openClient = useCallback(
    (client, highlightId = null) => {
      if (!client?._id) return;
      setSelectedClient(client);
      setHighlightBranch(highlightId);
      setBranchQuery("");
      setClientBranches([]);
      loadBranches(client._id);
    },
    [loadBranches]
  );

  const openBranchResult = useCallback(
    (branch) => {
      const parent = branch.client;
      // Si por algún motivo no viene poblado, no hacemos nada peligroso
      if (!parent || !parent._id) return;
      openClient(parent, branch._id);
    },
    [openClient]
  );

  const backToSearch = useCallback(() => {
    setSelectedClient(null);
    setClientBranches([]);
    setHighlightBranch(null);
    setBranchQuery("");
  }, []);

  // ───────────────────────────────────────────────────────────
  // FILTER BRANCHES (client-side, only over this client's branches)
  // ───────────────────────────────────────────────────────────
  const filteredBranches = useMemo(() => {
    const term = branchQuery.trim().toLowerCase();

    const sorted = [...clientBranches].sort((a, b) =>
      (a.name || "").localeCompare(b.name || "", "es", { sensitivity: "base" })
    );

    if (!term) return sorted;

    return sorted.filter(
      (b) =>
        b.name?.toLowerCase().includes(term) ||
        b.city?.toLowerCase().includes(term) ||
        b.address?.toLowerCase().includes(term) ||
        b.contactPhone?.toLowerCase().includes(term)
    );
  }, [clientBranches, branchQuery]);

  const citiesCount = useMemo(
    () => new Set(clientBranches.map((b) => b.city).filter(Boolean)).size,
    [clientBranches]
  );

  // ───────────────────────────────────────────────────────────
  // CLIENT CRUD
  // ───────────────────────────────────────────────────────────
  const createClient = useCallback(async (data) => {
    const clean = cleanFormData(data);
    const res = await clientsService.create(clean);
    const created = res.data?.data || res.data;
    setModalCliCreate(false);
    if (created?._id) {
      setSelectedClient(created);
      setClientBranches([]);
      setHighlightBranch(null);
      setBranchQuery("");
    }
  }, []);

  const updateClient = useCallback(async (id, data) => {
    const clean = filterFields(data, ["name", "contactName", "email", "phone"]);
    await clientsService.update(id, clean);
    setSelectedClient((p) => (p?._id === id ? { ...p, ...clean } : p));
    setResults((p) => ({
      ...p,
      clients: p.clients.map((c) => (c._id === id ? { ...c, ...clean } : c)),
    }));
    setModalCliEdit(false);
  }, []);

  const deleteClient = useCallback(
    async (id) => {
      await clientsService.delete(id);
      setResults((p) => ({
        ...p,
        clients: p.clients.filter((c) => c._id !== id),
        branches: p.branches.filter((b) => (b.client?._id || b.client) !== id),
      }));
      setModalCliDelete(false);
      if (selectedClient?._id === id) backToSearch();
    },
    [selectedClient, backToSearch]
  );

  // ───────────────────────────────────────────────────────────
  // BRANCH CRUD
  // ───────────────────────────────────────────────────────────
  const createBranch = useCallback(
    async (data) => {
      const payload = { ...data, client: selectedClient?._id || data.client };
      const res = await branchesService.create(payload);
      const nb = res.data?.data || res.data;
      setClientBranches((p) => [...p, nb]);
      setModalBrCreate(false);
      return { success: true };
    },
    [selectedClient]
  );

  const updateBranch = useCallback(async (id, data) => {
    await branchesService.update(id, data);
    setClientBranches((p) =>
      p.map((b) => (b._id === id ? { ...b, ...data } : b))
    );
    setModalBrEdit(false);
    return { success: true };
  }, []);

  const deleteBranch = useCallback(async (id) => {
    await branchesService.remove(id);
    setClientBranches((p) => p.filter((b) => b._id !== id));
    setModalBrDelete(false);
  }, []);

  // ───────────────────────────────────────────────────────────
  // HANDLERS
  // ───────────────────────────────────────────────────────────
  const handleEditClient = useCallback((client) => {
    setSelClient(client);
    setModalCliEdit(true);
  }, []);

  const handleDeleteClient = useCallback((client) => {
    setSelClient(client);
    setModalCliDelete(true);
  }, []);

  const handleEditBranch = useCallback((branch) => {
    setSelBranch(branch);
    setModalBrEdit(true);
  }, []);

  const handleDeleteBranch = useCallback((branch) => {
    setSelBranch(branch);
    setModalBrDelete(true);
  }, []);

  const handleOpenBranch = useCallback(
    (branch) => navigate(`/sucursales/${branch._id}`),
    [navigate]
  );

  // Para el formulario de sucursal sólo necesitamos el cliente actual,
  // así evitamos cargar el catálogo completo de clientes.
  const branchFormClients = useMemo(
    () => (selectedClient ? [selectedClient] : []),
    [selectedClient]
  );

  // ═══════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════
  return (
    <Box
      sx={{
        width: "100%",
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        p: 2,
        bgcolor: C.bg,
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      {selectedClient ? (
        // ═══════════════════════════════════════════════════════
        // DETAIL VIEW
        // ═══════════════════════════════════════════════════════
        <DetailView
          client={selectedClient}
          branches={clientBranches}
          filteredBranches={filteredBranches}
          branchesLoading={branchesLoading}
          citiesCount={citiesCount}
          branchQuery={branchQuery}
          setBranchQuery={setBranchQuery}
          highlightBranch={highlightBranch}
          onBack={backToSearch}
          onEditClient={handleEditClient}
          onDeleteClient={handleDeleteClient}
          onCreateBranch={() => setModalBrCreate(true)}
          onOpenBranch={handleOpenBranch}
          onEditBranch={handleEditBranch}
          onDeleteBranch={handleDeleteBranch}
        />
      ) : (
        // ═══════════════════════════════════════════════════════
        // SEARCH VIEW
        // ═══════════════════════════════════════════════════════
        <SearchView
          query={query}
          setQuery={setQuery}
          searching={searching}
          searchError={searchError}
          results={results}
          totalResults={totalResults}
          debounced={debounced}
          onNewClient={() => setModalCliCreate(true)}
          onOpenClient={openClient}
          onOpenBranch={openBranchResult}
        />
      )}

      {/* ─── MODALS ─── */}
      <ModalFormUniversal
        open={modalCliCreate}
        onClose={() => setModalCliCreate(false)}
        title="Nuevo Cliente"
        fields={CLIENT_FIELDS}
        initialData={{}}
        onSubmit={createClient}
      />

      <ModalFormUniversal
        open={modalCliEdit}
        onClose={() => setModalCliEdit(false)}
        title="Editar Cliente"
        fields={CLIENT_FIELDS}
        initialData={selClient || {}}
        onSubmit={(d) => updateClient(selClient._id, d)}
      />

      <ModalConfirmUniversal
        open={modalCliDelete}
        onClose={() => setModalCliDelete(false)}
        title="Eliminar cliente"
        message={`¿Eliminar ${selClient?.name}?`}
        confirmText="Eliminar"
        color="error"
        onConfirm={() => deleteClient(selClient._id)}
      />

      <ModalFormUniversal
        open={modalBrCreate}
        onClose={() => setModalBrCreate(false)}
        title="Nueva Sucursal"
        fields={getBranchFormFields(branchFormClients)}
        initialData={{ client: selectedClient?._id || "" }}
        onSubmit={createBranch}
      />

      <ModalFormUniversal
        open={modalBrEdit}
        onClose={() => setModalBrEdit(false)}
        title="Editar Sucursal"
        fields={getBranchEditFields(branchFormClients)}
        initialData={
          selBranch
            ? {
                ...selBranch,
                client:
                  selBranch.client?._id || selBranch.client || selectedClient?._id || "",
              }
            : {}
        }
        onSubmit={(d) =>
          updateBranch(selBranch._id, {
            ...d,
            client: selBranch.client?._id || selBranch.client || selectedClient?._id,
          })
        }
      />

      <ModalConfirmUniversal
        open={modalBrDelete}
        onClose={() => setModalBrDelete(false)}
        title="Eliminar sucursal"
        message={MESSAGES.deleteConfirm(selBranch?.name || "")}
        confirmText="Eliminar"
        color="error"
        onConfirm={() => deleteBranch(selBranch._id)}
      />
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────
// SEARCH VIEW
// ─────────────────────────────────────────────────────────────
function SearchView({
  query,
  setQuery,
  searching,
  searchError,
  results,
  totalResults,
  debounced,
  onNewClient,
  onOpenClient,
  onOpenBranch,
}) {
  const hasTerm = debounced.trim().length >= 2;
  const showEmptyHint = !hasTerm;
  const noResults = hasTerm && !searching && totalResults === 0 && !searchError;

  return (
    <Paper
      elevation={0}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        border: `1px solid ${C.border}`,
        borderRadius: 3,
        overflow: "hidden",
      }}
    >
      {/* HEADER + SEARCH */}
      <Box
        sx={{
          p: 3,
          borderBottom: `1px solid ${C.border}`,
          flexShrink: 0,
          background: `linear-gradient(180deg, ${C.bgSoft} 0%, #fff 100%)`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            mb: 2,
          }}
        >
          <Box>
            <Typography sx={{ fontSize: 22, fontWeight: 800, color: C.text }}>
              Clientes
            </Typography>
          </Box>

          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={onNewClient}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              bgcolor: C.primary,
              "&:hover": { bgcolor: C.primaryDark },
            }}
          >
            Nuevo Cliente
          </Button>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            px: 2,
            height: 52,
            border: `1.5px solid ${C.border}`,
            borderRadius: 3,
            bgcolor: "#fff",
            transition: "border-color .15s, box-shadow .15s",
            "&:focus-within": {
              borderColor: C.primary,
              boxShadow: `0 0 0 4px ${C.primary}1a`,
            },
          }}
        >
          <Search sx={{ color: C.textSoft }} />
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar por cliente o sucursal"
            style={{
              border: "none",
              outline: "none",
              width: "100%",
              marginLeft: 12,
              fontSize: 15,
              background: "transparent",
              color: C.text,
            }}
          />
          {searching && <CircularProgress size={18} sx={{ color: C.primary }} />}
          {!searching && query && (
            <IconButton size="small" onClick={() => setQuery("")} sx={{ color: C.textSoft }}>
              <Clear fontSize="small" />
            </IconButton>
          )}
        </Box>

        {hasTerm && !searchError && (
          <Typography sx={{ mt: 1.2, fontSize: 12.5, color: C.textSoft }}>
            {searching
              ? "Buscando en el servidor..."
              : `${totalResults} resultado${totalResults !== 1 ? "s" : ""} · ${
                  results.clients.length
                } cliente${results.clients.length !== 1 ? "s" : ""}, ${
                  results.branches.length
                } sucursal${results.branches.length !== 1 ? "es" : ""}`}
          </Typography>
        )}
      </Box>

      {/* BODY */}
      <Box sx={{ ...SCROLL, flex: 1, minHeight: 0, overflowY: "auto", p: 3 }}>
        {searchError && (
          <CenterMessage
            icon={<Search sx={{ fontSize: 40, color: C.danger }} />}
            title="Error de búsqueda"
            subtitle={searchError}
          />
        )}

        {showEmptyHint && (
          <CenterMessage
            icon={<LockOutlined sx={{ fontSize: 44, color: C.primary }} />}
            title="Empieza a buscar"
            subtitle="Una sola búsqueda encuentra clientes y sucursales."
          />
        )}

        {noResults && (
          <CenterMessage
            icon={<Search sx={{ fontSize: 44, color: C.textSoft }} />}
            title="Sin coincidencias"
            subtitle={`No encontramos clientes ni sucursales para "${debounced.trim()}"`}
          />
        )}

        {hasTerm && !searchError && totalResults > 0 && (
          <Fade in>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {results.clients.length > 0 && (
                <Box>
                  <SectionLabel
                    color={C.primary}
                    text={`Clientes (${results.clients.length})`}
                  />
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {results.clients.map((c) => (
                      <ResultRow
                        key={c._id}
                        type="client"
                        title={c.name}
                        subtitle={c.contactName || "Sin contacto"}
                        meta={c.phone || c.email}
                        onClick={() => onOpenClient(c)}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {results.branches.length > 0 && (
                <Box>
                  <SectionLabel
                    color={C.branch}
                    text={`Sucursales (${results.branches.length})`}
                  />
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {results.branches.map((b) => (
                      <ResultRow
                        key={b._id}
                        type="branch"
                        title={b.name}
                        subtitle={b.client?.name || "Cliente desconocido"}
                        meta={[b.city, b.contactPhone].filter(Boolean).join(" · ")}
                        onClick={() => onOpenBranch(b)}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </Fade>
        )}
      </Box>
    </Paper>
  );
}

// ─────────────────────────────────────────────────────────────
// DETAIL VIEW
// ─────────────────────────────────────────────────────────────
function DetailView({
  client,
  branches,
  filteredBranches,
  branchesLoading,
  citiesCount,
  branchQuery,
  setBranchQuery,
  highlightBranch,
  onBack,
  onEditClient,
  onDeleteClient,
  onCreateBranch,
  onOpenBranch,
  onEditBranch,
  onDeleteBranch,
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        border: `1px solid ${C.border}`,
        borderRadius: 3,
        overflow: "hidden",
      }}
    >
      {/* HEADER */}
      <Box sx={{ p: 3, borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
        <Button
          onClick={onBack}
          startIcon={<ArrowBack />}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            color: C.primary,
            mb: 2,
            "&:hover": { bgcolor: C.primarySoft },
          }}
        >
          Volver a la búsqueda
        </Button>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 2,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography noWrap sx={{ fontSize: 28, fontWeight: 800, color: C.text }}>
              {client.name}
            </Typography>

            <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 2 }}>
              {client.contactName && (
                <MetaItem icon={<PersonOutline />} text={client.contactName} />
              )}
              {client.email && <MetaItem icon={<MailOutline />} text={client.email} />}
              {client.phone && <MetaItem icon={<PhoneOutlined />} text={client.phone} />}
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
            <IconButton onClick={() => onEditClient(client)}>
              <Edit sx={{ color: C.primary }} />
            </IconButton>
            <IconButton onClick={() => onDeleteClient(client)}>
              <Delete sx={{ color: C.danger }} />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ mt: 3, display: "flex", gap: 1.5, flexWrap: "wrap" }}>
          <KPI label="Sucursales" value={branches.length} />
          <KPI label="Ciudades" value={citiesCount} />
        </Box>
      </Box>

      {/* BRANCHES */}
      <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {/* TOP */}
        <Box
          sx={{
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            borderBottom: `1px solid ${C.border}`,
            flexShrink: 0,
          }}
        >
          <Typography sx={{ fontWeight: 700, color: C.text }}>Sucursales</Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={onCreateBranch}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              bgcolor: C.primary,
              "&:hover": { bgcolor: C.primaryDark },
            }}
          >
            Nueva Sucursal
          </Button>
        </Box>

        {/* SEARCH BRANCHES */}
        <Box sx={{ p: 2, borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              px: 1.5,
              height: 42,
              border: `1px solid ${C.border}`,
              borderRadius: 2,
              bgcolor: "#fafafa",
            }}
          >
            <Search sx={{ fontSize: 18, color: C.textSoft }} />
            <input
              value={branchQuery}
              onChange={(e) => setBranchQuery(e.target.value)}
              placeholder="Filtrar sucursales de este cliente..."
              style={{
                border: "none",
                outline: "none",
                width: "100%",
                marginLeft: 8,
                fontSize: 14,
                background: "transparent",
              }}
            />
            {branchQuery && (
              <IconButton size="small" onClick={() => setBranchQuery("")} sx={{ color: C.textSoft }}>
                <Clear fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Box>

        {/* TABLE HEADER */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "minmax(0,1.5fr) minmax(100px,.7fr) minmax(100px,.7fr) 90px",
            gap: 2,
            px: 2,
            py: 1.5,
            bgcolor: "#fafafa",
            borderBottom: `1px solid ${C.border}`,
            flexShrink: 0,
          }}
        >
          {["SUCURSAL", "CIUDAD", "TELÉFONO", "ACCIONES"].map((h) => (
            <Typography key={h} sx={{ fontSize: 12, fontWeight: 700, color: C.textSoft }}>
              {h}
            </Typography>
          ))}
        </Box>

        {/* ROWS */}
        <Box sx={{ ...SCROLL, flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden" }}>
          {branchesLoading ? (
            <Box sx={{ p: 6, display: "flex", justifyContent: "center" }}>
              <CircularProgress size={26} sx={{ color: C.primary }} />
            </Box>
          ) : filteredBranches.length > 0 ? (
            filteredBranches.map((branch) => (
              <BranchRow
                key={branch._id}
                branch={branch}
                highlighted={highlightBranch === branch._id}
                onOpen={onOpenBranch}
                onEdit={onEditBranch}
                onDelete={onDeleteBranch}
              />
            ))
          ) : (
            <CenterMessage
              small
              icon={<Store sx={{ fontSize: 36, color: C.textSoft }} />}
              title={branchQuery ? "Sin coincidencias" : "Sin sucursales"}
              subtitle={
                branchQuery
                  ? "Ninguna sucursal coincide con el filtro"
                  : 'Crea la primera con "Nueva Sucursal"'
              }
            />
          )}
        </Box>
      </Box>
    </Paper>
  );
}

// ─────────────────────────────────────────────────────────────
// SMALL HELPERS
// ─────────────────────────────────────────────────────────────
function SectionLabel({ text, color }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.2 }}>
      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: color }} />
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 800,
          letterSpacing: 0.6,
          textTransform: "uppercase",
          color: C.textSoft,
        }}
      >
        {text}
      </Typography>
      <Divider sx={{ flex: 1, borderColor: C.border }} />
    </Box>
  );
}

function MetaItem({ icon, text }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, color: C.textSoft }}>
      {React.cloneElement(icon, { sx: { fontSize: 16 } })}
      <Typography sx={{ fontSize: 13.5, color: C.textSoft }}>{text}</Typography>
    </Box>
  );
}

function CenterMessage({ icon, title, subtitle, small }) {
  return (
    <Box
      sx={{
        height: small ? "auto" : "100%",
        minHeight: small ? 200 : 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        gap: 1.2,
        py: small ? 4 : 2,
        px: 3,
      }}
    >
      <Box
        sx={{
          width: 84,
          height: 84,
          borderRadius: "50%",
          bgcolor: C.bgSoft,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {icon}
      </Box>
      <Typography sx={{ fontSize: 18, fontWeight: 800, color: C.text }}>{title}</Typography>
      <Typography sx={{ fontSize: 13.5, color: C.textSoft, maxWidth: 460 }}>
        {subtitle}
      </Typography>
    </Box>
  );
}
